import {
  Component,
  ElementRef,
  effect,
  input,
  output,
  signal,
  computed,
  viewChild,
  viewChildren,
  HostListener,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  WorkflowNode,
  WorkflowNodeType,
  WorkflowNodeRuleType,
  NodePosition,
  CanvasViewport,
  nodeTypeLabelFor
} from '@formrails/workflow-designer/data-access';

/** Default size used until ResizeObserver reports the real dimensions. */
const NODE_WIDTH = 180;
const NODE_HEIGHT = 72;
/** Extra scrollable space beyond the furthest node, so there's room to drag into. */
const CANVAS_PADDING = 160;

interface NodeSize {
  width: number;
  height: number;
}

type Side = 'top' | 'right' | 'bottom' | 'left';

interface EdgeView {
  key: string;
  fromId: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  branch: 'true' | 'false';
}

interface PendingEdgeView {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  branch: 'true' | 'false';
}

interface NodeView {
  node: WorkflowNode;
  x: number;
  y: number;
  typeLabel: string;
  isCondition: boolean;
  hasOutput: boolean;
}

/** Rendered size of a node; emitted for every node whenever any size changes. */
export interface NodeMeasurement {
  id: string;
  width: number;
  height: number;
}

/** Emitted when a node is dragged to a new position. */
export interface NodeMove {
  id: string;
  position: NodePosition;
}

/** Emitted when the user wires one node into another by dragging from a handle. */
export interface NodeConnect {
  fromId: string;
  toId: string;
  branch: 'true' | 'false';
}

/** Emitted when the user removes an edge (selected arrow + Delete/Backspace). */
export interface EdgeDelete {
  fromId: string;
  branch: 'true' | 'false';
}

/**
 * Visual canvas for a workflow graph. Nodes are absolutely-positioned boxes;
 * edges are SVG lines anchored to whichever side of each node best matches the
 * direction of travel (auto direction-aware). Each node exposes one or two
 * draggable output handles — dragging onto a target node wires the source's
 * `successorTrue` or `successorFalse`.
 */
@Component({
  selector: 'app-workflow-canvas',
  standalone: true,
  imports: [],
  templateUrl: './workflow-canvas.component.html',
  styleUrl: './workflow-canvas.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkflowCanvasComponent {
  /** The scroll container; pointer maths is relative to its scrolled content. */
  private readonly canvasEl = viewChild.required<ElementRef<HTMLDivElement>>('canvasEl');

  /** Background drag-to-pan in progress: where the pointer and scroll started. */
  private panStart: { pointerX: number; pointerY: number; scrollLeft: number; scrollTop: number } | null =
    null;
  private readonly panning = signal(false);
  readonly isPanning = this.panning.asReadonly();

  readonly nodes = input<WorkflowNode[]>([]);
  readonly positions = input<Record<string, NodePosition>>({});
  readonly selectedNodeId = input<string | null>(null);

  readonly selectNode = output<string>();
  readonly moveNode = output<NodeMove>();
  readonly connectNodes = output<NodeConnect>();
  readonly deleteEdge = output<EdgeDelete>();
  readonly deleteNode = output<string>();
  readonly measureNodes = output<NodeMeasurement[]>();
  /** Canvas size, so layout can centre the graph in it. */
  readonly measureCanvas = output<CanvasViewport>();


  /** Live position override for the node currently being dragged. */
  private readonly dragId = signal<string | null>(null);
  private readonly dragPos = signal<NodePosition | null>(null);
  private dragOffset: NodePosition = { x: 0, y: 0 };

  /** Live state while the user is dragging an edge from an output handle. */
  private readonly edgeDrag = signal<{
    fromId: string;
    branch: 'true' | 'false';
    pointer: NodePosition;
  } | null>(null);

  private readonly hoverTargetId = signal<string | null>(null);

  /** Live per-node rendered size, populated by a ResizeObserver. */
  private readonly nodeSizes = signal<Map<string, NodeSize>>(new Map());
  private readonly nodeRefs = viewChildren<ElementRef<HTMLDivElement>>('nodeEl');
  private resizeObserver: ResizeObserver | null = null;
  private canvasObserver: ResizeObserver | null = null;

  constructor() {
    effect(() => {
      const canvas = this.canvasEl().nativeElement;
      this.canvasObserver?.disconnect();
      if (typeof ResizeObserver === 'undefined') return;
      const obs = new ResizeObserver(() =>
        this.measureCanvas.emit({ width: canvas.clientWidth, height: canvas.clientHeight })
      );
      obs.observe(canvas);
      this.canvasObserver = obs;
    });

    effect(() => {
      const refs = this.nodeRefs();
      this.resizeObserver?.disconnect();
      if (typeof ResizeObserver === 'undefined') return;
      const obs = new ResizeObserver(entries => {
        const next = new Map(this.nodeSizes());
        let changed = false;
        for (const entry of entries) {
          const el = entry.target as HTMLElement;
          const id = el.dataset['id'];
          if (!id) continue;
          // Use offset dimensions (border-box) so the anchor math matches what
          // the user sees. entry.contentRect is the content-box and excludes
          // padding/border, which would put anchors inside the visible card.
          const width = el.offsetWidth;
          const height = el.offsetHeight;
          const prev = next.get(id);
          if (!prev || prev.width !== width || prev.height !== height) {
            next.set(id, { width, height });
            changed = true;
          }
        }
        if (changed) {
          this.nodeSizes.set(next);
          // Layout needs every card's size to space columns, not just the ones that changed.
          this.measureNodes.emit([...next].map(([id, size]) => ({ id, ...size })));
        }
      });
      for (const ref of refs) obs.observe(ref.nativeElement);
      this.resizeObserver = obs;
    });
  }

  private sizeOf(id: string): NodeSize {
    return this.nodeSizes().get(id) ?? { width: NODE_WIDTH, height: NODE_HEIGHT };
  }

  /** Currently-selected edge, keyed by `${fromId}-t` or `${fromId}-f`. */
  private readonly selectedEdgeKeySig = signal<string | null>(null);
  readonly selectedEdgeKey = this.selectedEdgeKeySig.asReadonly();

  private posOf(id: string): NodePosition {
    if (this.dragId() === id && this.dragPos()) return this.dragPos() as NodePosition;
    return this.positions()[id] ?? { x: 0, y: 0 };
  }

  readonly nodeViews = computed<NodeView[]>(() =>
    this.nodes().map(node => {
      const p = this.posOf(node.id);
      return {
        node,
        x: p.x,
        y: p.y,
        typeLabel: nodeTypeLabelFor(node),
        isCondition:
          node.type === WorkflowNodeType.Rule &&
          node.ruleType === WorkflowNodeRuleType.Condition,
        hasOutput: node.type !== WorkflowNodeType.Terminator
      };
    })
  );

  /** Size of the scrollable area: every node, plus room to drag past the furthest one. */
  readonly contentSize = computed(() => {
    let width = 0;
    let height = 0;
    for (const node of this.nodes()) {
      const pos = this.posOf(node.id);
      const size = this.sizeOf(node.id);
      width = Math.max(width, pos.x + size.width);
      height = Math.max(height, pos.y + size.height);
    }
    return { width: width + CANVAS_PADDING, height: height + CANVAS_PADDING };
  });

  readonly edges = computed<EdgeView[]>(() => {
    const byId = new Map(this.nodes().map(n => [n.id, n]));
    const out: EdgeView[] = [];
    for (const n of this.nodes()) {
      if (n.successorTrue && n.successorTrue !== n.id && byId.has(n.successorTrue)) {
        out.push(edgeBetween(
          `${n.id}-t`,
          n.id,
          this.posOf(n.id), this.sizeOf(n.id),
          this.posOf(n.successorTrue), this.sizeOf(n.successorTrue),
          'true'
        ));
      }
      if (n.successorFalse && byId.has(n.successorFalse)) {
        out.push(edgeBetween(
          `${n.id}-f`,
          n.id,
          this.posOf(n.id), this.sizeOf(n.id),
          this.posOf(n.successorFalse), this.sizeOf(n.successorFalse),
          'false'
        ));
      }
    }
    return out;
  });

  readonly pendingEdge = computed<PendingEdgeView | null>(() => {
    const drag = this.edgeDrag();
    if (!drag) return null;
    const from = this.posOf(drag.fromId);
    const fromSize = this.sizeOf(drag.fromId);
    const target: NodePosition = {
      x: drag.pointer.x - fromSize.width / 2,
      y: drag.pointer.y - fromSize.height / 2
    };
    const { fromSide } = pickSides(from, fromSize, target, fromSize);
    const a = anchorOf(from, fromSize, fromSide);
    return { x1: a.x, y1: a.y, x2: drag.pointer.x, y2: drag.pointer.y, branch: drag.branch };
  });

  readonly hoverTarget = this.hoverTargetId.asReadonly();

  isSelected(id: string): boolean {
    return this.selectedNodeId() === id;
  }

  onNodePointerDown(event: PointerEvent, node: WorkflowNode): void {
    event.stopPropagation();
    this.selectNode.emit(node.id);
    const point = this.toCanvasPoint(event);
    const current = this.positions()[node.id] ?? { x: 0, y: 0 };
    this.dragOffset = {
      x: point.x - current.x,
      y: point.y - current.y
    };
    this.dragId.set(node.id);
    this.dragPos.set(current);
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
  }

  onHandlePointerDown(event: PointerEvent, node: WorkflowNode, branch: 'true' | 'false'): void {
    event.stopPropagation();
    event.preventDefault();
    this.edgeDrag.set({
      fromId: node.id,
      branch,
      pointer: this.toCanvasPoint(event)
    });
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
  }

  onPointerMove(event: PointerEvent): void {
    if (this.panStart) {
      const canvas = this.canvasEl().nativeElement;
      canvas.scrollLeft = this.panStart.scrollLeft - (event.clientX - this.panStart.pointerX);
      canvas.scrollTop = this.panStart.scrollTop - (event.clientY - this.panStart.pointerY);
      return;
    }

    const { x, y } = this.toCanvasPoint(event);

    if (this.dragId()) {
      this.dragPos.set({
        x: Math.max(0, x - this.dragOffset.x),
        y: Math.max(0, y - this.dragOffset.y)
      });
      return;
    }

    const edge = this.edgeDrag();
    if (edge) {
      this.edgeDrag.set({ ...edge, pointer: { x, y } });
      this.hoverTargetId.set(this.hitTestNode(x, y, edge.fromId));
    }
  }

  onPointerUp(event: PointerEvent): void {
    if (this.panStart) {
      this.endPan();
      return;
    }

    const id = this.dragId();
    const pos = this.dragPos();
    if (id && pos) this.moveNode.emit({ id, position: pos });
    this.dragId.set(null);
    this.dragPos.set(null);

    const edge = this.edgeDrag();
    if (edge) {
      const { x, y } = this.toCanvasPoint(event);
      const targetId = this.hitTestNode(x, y, edge.fromId);
      if (targetId) {
        this.connectNodes.emit({ fromId: edge.fromId, toId: targetId, branch: edge.branch });
      }
      this.edgeDrag.set(null);
      this.hoverTargetId.set(null);
    }
  }

  onBackgroundPointerDown(event: PointerEvent): void {
    if (this.edgeDrag()) return;
    this.selectedEdgeKeySig.set(null);
    this.selectNode.emit('');

    // Drag on empty canvas pans the view. Primary button only, and not when
    // the press lands on a scrollbar (outside the client area).
    const canvas = this.canvasEl().nativeElement;
    const rect = canvas.getBoundingClientRect();
    const onScrollbar =
      event.clientX - rect.left > canvas.clientWidth || event.clientY - rect.top > canvas.clientHeight;
    if (event.button !== 0 || onScrollbar) return;

    event.preventDefault();
    this.panStart = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      scrollLeft: canvas.scrollLeft,
      scrollTop: canvas.scrollTop
    };
    this.panning.set(true);
    canvas.setPointerCapture?.(event.pointerId);
  }

  /** The browser took the pointer away (e.g. a system gesture): abandon whatever was in progress. */
  onPointerCancel(): void {
    this.endPan();
    this.dragId.set(null);
    this.dragPos.set(null);
    this.edgeDrag.set(null);
    this.hoverTargetId.set(null);
  }

  onEdgePointerDown(event: PointerEvent, edge: EdgeView): void {
    event.stopPropagation();
    this.selectedEdgeKeySig.set(edge.key);
    this.selectNode.emit('');
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Delete' && event.key !== 'Backspace') return;
    const target = event.target as HTMLElement | null;
    if (target && target.matches('input, textarea, select, [contenteditable="true"]')) return;

    const edgeKey = this.selectedEdgeKeySig();
    if (edgeKey) {
      const edge = this.edges().find(e => e.key === edgeKey);
      if (!edge) return;
      event.preventDefault();
      this.selectedEdgeKeySig.set(null);
      this.deleteEdge.emit({ fromId: edge.fromId, branch: edge.branch });
      return;
    }

    const nodeId = this.selectedNodeId();
    if (nodeId) {
      event.preventDefault();
      this.deleteNode.emit(nodeId);
    }
  }

  private endPan(): void {
    this.panStart = null;
    this.panning.set(false);
  }

  /** Pointer position in canvas content coordinates, allowing for how far it's scrolled. */
  private toCanvasPoint(event: PointerEvent): NodePosition {
    const canvas = this.canvasEl().nativeElement;
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left + canvas.scrollLeft,
      y: event.clientY - rect.top + canvas.scrollTop
    };
  }

  private hitTestNode(x: number, y: number, excludeId: string): string | null {
    for (const n of this.nodes()) {
      if (n.id === excludeId) continue;
      const p = this.posOf(n.id);
      const s = this.sizeOf(n.id);
      if (x >= p.x && x <= p.x + s.width && y >= p.y && y <= p.y + s.height) {
        return n.id;
      }
    }
    return null;
  }
}

function pickSides(
  from: NodePosition,
  fromSize: NodeSize,
  to: NodePosition,
  toSize: NodeSize
): { fromSide: Side; toSide: Side } {
  const fcx = from.x + fromSize.width / 2;
  const fcy = from.y + fromSize.height / 2;
  const tcx = to.x + toSize.width / 2;
  const tcy = to.y + toSize.height / 2;
  const dx = tcx - fcx;
  const dy = tcy - fcy;
  if (Math.abs(dx) > Math.abs(dy)) {
    return dx > 0
      ? { fromSide: 'right', toSide: 'left' }
      : { fromSide: 'left', toSide: 'right' };
  }
  return dy > 0
    ? { fromSide: 'bottom', toSide: 'top' }
    : { fromSide: 'top', toSide: 'bottom' };
}

function anchorOf(pos: NodePosition, size: NodeSize, side: Side): NodePosition {
  switch (side) {
    case 'top':
      return { x: pos.x + size.width / 2, y: pos.y };
    case 'right':
      return { x: pos.x + size.width, y: pos.y + size.height / 2 };
    case 'bottom':
      return { x: pos.x + size.width / 2, y: pos.y + size.height };
    case 'left':
      return { x: pos.x, y: pos.y + size.height / 2 };
  }
}

function edgeBetween(
  key: string,
  fromId: string,
  from: NodePosition,
  fromSize: NodeSize,
  to: NodePosition,
  toSize: NodeSize,
  branch: 'true' | 'false'
): EdgeView {
  const { fromSide, toSide } = pickSides(from, fromSize, to, toSize);
  const a = anchorOf(from, fromSize, fromSide);
  const b = anchorOf(to, toSize, toSide);
  return { key, fromId, x1: a.x, y1: a.y, x2: b.x, y2: b.y, branch };
}
